import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";

const DEFAULTS = {
  brand_name: "This That For Me",
  tagline: "Curated by the creator",
  logo_url: null,
  heading_font: "Fraunces",
  body_font: "Manrope",
  category_font: "Fraunces",
  accent_color: "#D4AF37",
  instagram_main: "@ThisThatForMe",
  instagram_secondary: "@TinyTunesDancer",
  tiktok_url: "",
  etsy_url: "",
  contact_email: "hello@thisthatforme.shop",
  printify_api_token: "",
  printify_shop_id: "",
  printful_api_token: "",
  lead_magnet_url: "https://example.com/downloads/free-5-prompts.pdf",
  lead_magnet_title: "5 free Nano Banana prompts",
  resend_api_key: "",
  from_email: "hello@thisthatforme.shop",
};

const SettingsContext = createContext({ settings: DEFAULTS, refresh: () => {} });

function injectFont(family) {
  if (!family) return;
  const id = `gf-${family.replace(/\s+/g, "-")}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(family)}:ital,wght@0,300;0,400;0,500;0,600;0,700;0,800;1,400&display=swap`;
  document.head.appendChild(link);
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState(DEFAULTS);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings({ ...DEFAULTS, ...data });
    } catch {
      setSettings(DEFAULTS);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    injectFont(settings.heading_font);
    injectFont(settings.body_font);
    injectFont(settings.category_font);
    document.documentElement.style.setProperty("--heading-font", `'${settings.heading_font}', Georgia, serif`);
    document.documentElement.style.setProperty("--body-font", `'${settings.body_font}', system-ui, sans-serif`);
    document.documentElement.style.setProperty("--category-font", `'${settings.category_font || settings.heading_font}', Georgia, serif`);
    document.documentElement.style.setProperty("--accent", settings.accent_color);
    document.title = `${settings.brand_name} — Creator Marketplace`;
  }, [settings]);

  const value = useMemo(() => ({ settings, refresh, setSettings }), [settings, refresh]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
